/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { makeSelectOptions } from 'utils/makeSelectOptions';
import { emailPattern, passwordPattern, passwordsMatchValidation } from 'utils/patterns';
import SelectComponent from 'components/Inputs/SelectComponent';
import { ControlledInput } from 'components/Inputs/ControlledInput/ControlledInput';
import { AdminUsersRole, IAdminUserForm } from 'stores/users/types';
import { adminUsersPopupTranslations } from './translations';
import { rolesOptions } from './constants';
import { AdminUsersPopupTransFields } from './types';
import styles from './adminUserPopUp.module.scss';

const AdminUserPopUp: FC = observer(function () {
  const {
    popUpStore: { title, description, buttonLabel, closePopUp },
    usersStore: { curAdminUser, addAdminUser, editAdminUser, setCurAdminUser, getUsersCountryCodes, usersCountryCodes },
  } = useStore();

  const { t } = useMyTranslations<AdminUsersPopupTransFields>([adminUsersPopupTranslations.name]);

  const formMethods = useForm<IAdminUserForm>({ defaultValues: curAdminUser || {} });
  const { handleSubmit, setValue, watch } = formMethods;

  const { isLoading, mutate: getCC } = useMutation(getUsersCountryCodes, {
    mutationKey: 'getUsersCountryCodes',
  });

  useEffect(() => {
    if (!usersCountryCodes) getCC();
    return () => setCurAdminUser(null);
  }, []);

  const { mutate: editMutation, isLoading: isLoadingEdit } = useMutation(editAdminUser, {
    mutationKey: 'editAdminUser',
    onSuccess: () => {
      closePopUp();
    },
  });

  const { mutate: addMutation, isLoading: isLoadingAdd } = useMutation(addAdminUser, {
    mutationKey: 'addAdminUser',
    onSuccess: () => {
      closePopUp();
    },
  });

  const onSubmitHandler = useCallback(
    (formData: IAdminUserForm) => {
      if (curAdminUser) editMutation({ ...curAdminUser, ...formData });
      else addMutation(formData);
    },
    [curAdminUser]
  );

  return (
    <FormProvider {...(formMethods as any)}>
      <form className={styles.form}>
        <p className={styles.title}>{title}</p>
        <p className={styles.desc}>{description}</p>
        <ControlledInput name='name' type='text' required label={t('name')} />
        <ControlledInput name='email' type='text' label={t('email')} registerPattern={emailPattern} required />
        <ControlledInput
          name='password'
          type='password'
          label={t('password')}
          registerPattern={passwordPattern}
          required={!curAdminUser}
          autoComplete={'new-password'}
        />
        <ControlledInput
          name={`repPassword`}
          type='password'
          label={t('repPassword')}
          validation={passwordsMatchValidation(watch('password'))}
          required={!curAdminUser}
        />
        <SelectComponent
          name='role'
          label={t('role')}
          options={makeSelectOptions(rolesOptions)}
          onSelect={(val) => {
            setValue('role', val.value as AdminUsersRole);
          }}
          required
          maxMenuHeight={120}
        />
        <SelectComponent
          name='usersCountryCodes'
          label={t('usersCountryCodes')}
          options={usersCountryCodes || []}
          required
          maxMenuHeight={120}
          isDisabled={isLoading}
          isMulti
          optionValue='countryCode'
          optionLabel='name'
        />
        <footer className={styles.footer}>
          <button
            type='reset'
            className={styles.btnReset}
            onClick={closePopUp}
            disabled={isLoading || isLoadingAdd || isLoadingEdit}>
            {t('cancel')}
          </button>
          <button
            type='button'
            className={styles.btnSubmit}
            onClick={handleSubmit(onSubmitHandler)}
            disabled={isLoading || isLoadingAdd || isLoadingEdit}>
            {buttonLabel}
          </button>
        </footer>
      </form>
    </FormProvider>
  );
});

export default AdminUserPopUp;
